import React, { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Select, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CatalogueAsset,
  BondFamily,
  CatalogueEntry,
  CatalogueIndex,
  FilterOption,
  FilterState,
  FamilySelectionContext,
} from './types';
import {
  buildCatalogue,
  facetCounts,
  catalogueEntries,
  matches,
} from './filters';
import AssetDetails from './AssetDetails';
import BondFamilyDrawer from './BondFamilyDrawer';
import AdditionReview from './AdditionReview';
import CatalogueTable from './CatalogueTable';
import { instrumentColumns } from './instrumentColumns';
import './catalogue.css';
import './instrumentTables.css';
import {
  VisibilityCheckbox,
  VisibilityToolbar,
  VisibilityController,
} from '../instrumentVisibility';

type Props = {
  data: CatalogueIndex;
  admin?: boolean;
  restrictToCurated?: boolean;
  showAssetClass?: boolean;
  reload: () => void;
  toolbarActions?: React.ReactNode;
  selected?: number[];
  onAdd?: (ids: number[]) => void;
  onRemove?: (ids: number[]) => void;
  disabled?: boolean;
  selectedExtra?: (assetId: number) => React.ReactNode;
  selectedAction?: (assetId: number) => React.ReactNode;
  familyContext?: FamilySelectionContext;
  startedSessionId?: number;
  visibility?: VisibilityController;
};

export default function CatalogueBrowser({
  data,
  admin = false,
  restrictToCurated = false,
  showAssetClass = true,
  reload,
  toolbarActions,
  selected,
  onAdd,
  onRemove,
  disabled,
  selectedExtra,
  selectedAction,
  familyContext,
  startedSessionId,
  visibility,
}: Props) {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({});
  const [onlySelected, setOnlySelected] = useState(
    startedSessionId !== undefined,
  );
  const [curatedOnly, setCuratedOnly] = useState(true);
  const [archives, setArchives] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [issuedBy, setIssuedBy] = useState('');
  const [maturesAfter, setMaturesAfter] = useState('');
  const [detail, setDetail] = useState<CatalogueAsset | null>(null);
  const [bulk, setBulk] = useState<CatalogueEntry[] | null>(null);
  const [family, setFamily] = useState<BondFamily | null>(null);
  React.useEffect(() => {
    setBulk(null);
  }, [
    familyContext?.key,
    familyContext?.historical,
    familyContext?.live,
    disabled,
  ]);
  const [collectionNote, setCollectionNote] = useState<FilterOption | null>(
    null,
  );
  const model = useMemo(() => buildCatalogue(data, archives), [data, archives]);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const selectedFamilies = useMemo(
    () =>
      new Set(
        model.rows.flatMap(row =>
          row.bond_family && selectedSet.has(row.id)
            ? [row.bond_family.id]
            : [],
        ),
      ),
    [model, selectedSet],
  );
  const hasCuration = model.rows.some(row => row.curated);
  const effectiveCurated = restrictToCurated || (curatedOnly && hasCuration);
  // Scope once, before both matching and facet counting. This does not mark
  // saved selections as curated or make other non-catalogued assets available.
  const scopedRows = useMemo(
    () =>
      model.rows.filter(
        row =>
          !effectiveCurated ||
          row.curated ||
          (startedSessionId !== undefined && selectedSet.has(row.id)),
      ),
    [model, effectiveCurated, startedSessionId, selectedSet],
  );
  const candidateRows = useMemo(() => {
    const words = query.toLowerCase().trim().split(/\s+/);
    // Apply name/ticker search once, before both results and facet counts.
    return scopedRows.filter(row => {
      const searchText = [row.name, row.ticker, row.bond_family?.code]
        .join(' ')
        .toLowerCase();
      return (
        (!onlySelected ||
          selectedSet.has(row.id) ||
          (familyContext?.live &&
            row.bond_family &&
            selectedFamilies.has(row.bond_family.id))) &&
        (!issuedBy || (row.issue_date && row.issue_date <= issuedBy)) &&
        (!maturesAfter ||
          (row.maturity_date && row.maturity_date >= maturesAfter)) &&
        words.every(word => searchText.includes(word))
      );
    });
  }, [
    scopedRows,
    onlySelected,
    selectedSet,
    selectedFamilies,
    familyContext?.live,
    issuedBy,
    maturesAfter,
    query,
  ]);
  const rows = useMemo(
    () => candidateRows.filter(row => matches(row, filters)),
    [candidateRows, filters],
  );
  const counts = useMemo(
    () => facetCounts(candidateRows, filters, '', false),
    [candidateRows, filters],
  );
  const shownRows = useMemo(() => catalogueEntries(rows), [rows]);
  const allEntries = useMemo(
    () =>
      new Map(catalogueEntries(model.rows).map(entry => [entry.key, entry])),
    [model],
  );
  const byId = useMemo(
    () => new Map(model.rows.map(row => [row.id, row])),
    [model],
  );
  const entryIds = (entry: CatalogueEntry) =>
    (allEntries.get(entry.key) || entry).members.map(row => row.id);
  const isLiveFamily = (entry: CatalogueEntry) =>
    Boolean(entry.family && familyContext?.live);
  const entrySelected = (entry: CatalogueEntry) =>
    isLiveFamily(entry)
      ? entryIds(entry).some(id => selectedSet.has(id))
      : entryIds(entry).every(id => selectedSet.has(id));
  const entryPartial = (entry: CatalogueEntry) =>
    !isLiveFamily(entry) &&
    !entrySelected(entry) &&
    entryIds(entry).some(id => selectedSet.has(id));
  const entryAddIds = (entry: CatalogueEntry) => {
    if (!isLiveFamily(entry)) return entryIds(entry);
    // The existing writer accepts a member ID as the family's selection handle.
    // It saves family intent; the actual playable issue is resolved at Start.
    const members = (allEntries.get(entry.key) || entry).members;
    const latest = members.reduce((candidate, row) =>
      (row.issue_date || '') > (candidate.issue_date || '') ? row : candidate,
    );
    return [latest.id];
  };
  const entryAddable = (entry: CatalogueEntry) =>
    !entry.family ||
    !familyContext ||
    familyContext.historical ||
    familyContext.live;
  const canInspectFamily = (entry: CatalogueEntry) =>
    !familyContext || entryIds(entry).some(id => selectedSet.has(id));
  const toggle = (facet: string, key: string) =>
    setFilters(current => ({
      ...current,
      [facet]: current[facet]?.includes(key)
        ? current[facet].filter(value => value !== key)
        : [...(current[facet] || []), key],
    }));
  const hasFamilies = shownRows.some(entry => entry.family);
  const columns: ColumnsType<CatalogueEntry> =
    instrumentColumns<CatalogueEntry>({
      options: model.options,
      showAssetClass,
      defaultExposureSort: true,
      members: entry => entry.members,
      family: entry => entry.family,
      canOpen: entry =>
        !isLiveFamily(entry) &&
        (!entry.family ||
          canInspectFamily(entry) ||
          (!disabled && entryAddable(entry))),
      onOpen: entry => {
        if (!entry.family) setDetail(entry.members[0]);
        else if (canInspectFamily(entry)) setFamily(entry.family);
        else if (!disabled && entryAddable(entry)) setBulk([entry]);
      },
      extraColumns:
        hasFamilies || onRemove || selectedExtra || selectedAction
          ? [
              {
                title: 'Actions',
                key: 'actions',
                render: (_: unknown, entry: CatalogueEntry) => {
                  const ids = entryIds(entry);
                  const selectedIds = ids.filter(id => selectedSet.has(id));
                  if (
                    (!entry.family || isLiveFamily(entry)) &&
                    !selectedIds.length
                  )
                    return null;
                  const asset = entry.members[0];
                  return (
                    <div className="ac-issue-controls">
                      <div className="ac-table-line">
                        {entry.family && !isLiveFamily(entry) && (
                          <>
                            {canInspectFamily(entry) && (
                              <Button
                                size="small"
                                type="link"
                                onClick={() => setFamily(entry.family)}
                              >
                                {familyContext
                                  ? 'Selected issues'
                                  : 'View issues'}
                              </Button>
                            )}
                            {onAdd && (
                              <Button
                                size="small"
                                disabled={disabled || !entryAddable(entry)}
                                title={
                                  entryAddable(entry)
                                    ? undefined
                                    : 'Create a new real-time session or blueprint to select synthetic bond families. Existing sessions keep their original behavior.'
                                }
                                onClick={() => setBulk([entry])}
                                aria-label={`Prepare ${entry.family.code}`}
                              >
                                {entryAddable(entry)
                                  ? 'Prepare'
                                  : 'New session required'}
                              </Button>
                            )}
                          </>
                        )}
                        {onRemove && selectedIds.length > 0 && (
                          <Button
                            size="small"
                            disabled={disabled}
                            aria-label={`Remove ${entry.family?.code || asset.ticker}`}
                            onClick={() => onRemove(selectedIds)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      {entry.family &&
                        !isLiveFamily(entry) &&
                        selectedIds.length > 0 && (
                          <small className="ac-muted">
                            {familyContext
                              ? `${selectedIds.length} selected issues`
                              : selectedIds.length < ids.length
                                ? 'Partial issue selection'
                                : 'All issues selected'}
                          </small>
                        )}
                      {(!entry.family || isLiveFamily(entry)) &&
                        selectedExtra?.(selectedIds[0] ?? asset.id)}
                      {!entry.family && selectedAction?.(asset.id)}
                      {startedSessionId !== undefined &&
                        entry.members.some(
                          row => selectedSet.has(row.id) && !row.curated,
                        ) && (
                          <small className="ac-muted">
                            {entry.members.every(row => !row.curated)
                              ? 'Outside catalogue'
                              : 'Includes selections outside catalogue'}
                          </small>
                        )}
                    </div>
                  );
                },
              },
            ]
          : [],
    });
  if (visibility?.enabled)
    columns.push({
      title: 'Player visibility',
      key: 'player-visibility',
      width: 220,
      render: (_, entry) => {
        const id = entryIds(entry).find(id => selectedSet.has(id));
        return id === undefined ? null : (
          <VisibilityCheckbox visibility={visibility} assetId={id} />
        );
      },
    });
  const facetRows = [
    { key: 'tier', label: 'Tier' },
    ...data.facets.filter(f => ['exposure', 'form', 'sector'].includes(f.key)),
  ];
  const collectionOptions = model.options.filter(
    option => option.facet === 'collection',
  );
  const moreFacets = data.facets.filter(
    f => !['exposure', 'form', 'sector'].includes(f.key),
  );
  if (model.options.some(option => option.facet === 'basket'))
    moreFacets.push({ key: 'basket', label: 'Other baskets' });
  if (admin) moreFacets.push({ key: 'availability', label: 'Availability' });
  return (
    <div className="ac-browser">
      <div className="ac-toolbar">
        {!restrictToCurated && (
          <div className="ac-scope" role="group" aria-label="Catalogue scope">
            <button
              aria-pressed={curatedOnly}
              onClick={() => setCuratedOnly(true)}
            >
              Curated catalogue
            </button>
            <button
              aria-pressed={!curatedOnly}
              onClick={() => setCuratedOnly(false)}
            >
              All PM assets
            </button>
          </div>
        )}
        <Input.Search
          size="small"
          aria-label="Search assets"
          placeholder="Search name or ticker"
          value={query}
          onChange={e => setQuery(e.target.value)}
          allowClear
        />
        <Button
          size="small"
          aria-expanded={advanced}
          onClick={() => setAdvanced(value => !value)}
        >
          {advanced ? 'Fewer filters' : 'More filters'}
        </Button>
        <Button
          size="small"
          onClick={() => {
            setFilters({});
            setOnlySelected(false);
            setQuery('');
            setIssuedBy('');
            setMaturesAfter('');
          }}
        >
          Clear filters
        </Button>
        {toolbarActions}
        {admin && (
          <Button
            size="small"
            href="/dashboard/catalogue-studio"
            target="_blank"
            rel="noopener noreferrer"
          >
            Edit catalogue
          </Button>
        )}
      </div>
      {visibility && (
        <VisibilityToolbar
          visibility={visibility}
          filteredIds={shownRows.flatMap(entry =>
            entryIds(entry).filter(id => selectedSet.has(id)),
          )}
        />
      )}
      {!hasCuration && (
        <Alert
          type="info"
          showIcon
          title={
            restrictToCurated
              ? startedSessionId !== undefined && scopedRows.length > 0
                ? 'No curated instruments are available. Showing assets already in this session.'
                : 'No curated instruments are available.'
              : 'Catalogue curation is not available yet. Showing existing PM assets.'
          }
        />
      )}
      <div className="ac-filters">
        {facetRows.map(facet => (
          <div className="ac-facet" key={facet.key}>
            <span className="ac-facet-label">
              {facet.key === 'exposure'
                ? 'Exposure'
                : facet.key === 'form'
                  ? 'Expression form'
                  : facet.label}
            </span>
            <div className="ac-pills">
              {model.options
                .filter(option => option.facet === facet.key)
                .map(option => (
                  <button
                    key={option.key}
                    title={option.label}
                    aria-pressed={Boolean(
                      filters[facet.key]?.includes(option.key),
                    )}
                    disabled={
                      !counts[option.key] &&
                      !filters[facet.key]?.includes(option.key)
                    }
                    onClick={() => toggle(facet.key, option.key)}
                  >
                    {facet.key === 'tier'
                      ? option.label.match(/^Tier\s+\d+/i)?.[0] || option.label
                      : option.label}
                    <span>{counts[option.key] || 0}</span>
                  </button>
                ))}
            </div>
          </div>
        ))}
        {collectionOptions.length > 0 && (
          <div className="ac-facet">
            <span className="ac-facet-label">Clusters</span>
            <Select
              className="ac-collection-filter"
              size="small"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              aria-label="Clusters"
              placeholder="Choose clusters…"
              maxTagCount="responsive"
              maxTagTextLength={36}
              value={filters.collection || []}
              onChange={values =>
                setFilters(current => ({ ...current, collection: values }))
              }
              options={collectionOptions.map(option => ({
                value: option.key,
                label: `${option.label} (${counts[option.key] || 0})`,
                disabled:
                  !counts[option.key] &&
                  !filters.collection?.includes(option.key),
              }))}
            />
          </div>
        )}
        {advanced && (
          <div className="ac-advanced">
            {moreFacets.map(facet => (
              <label key={facet.key}>
                {facet.label}
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  aria-label={facet.label}
                  value={filters[facet.key] || []}
                  onChange={values =>
                    setFilters(current => ({ ...current, [facet.key]: values }))
                  }
                  options={model.options
                    .filter(o => o.facet === facet.key)
                    .map(o => ({
                      value: o.key,
                      label: `${o.label} (${counts[o.key] || 0})`,
                    }))}
                />
              </label>
            ))}
            <Checkbox
              checked={archives}
              onChange={e => setArchives(e.target.checked)}
            >
              Include archived collections
            </Checkbox>
            <label>
              Issued on or before
              <Input
                type="date"
                value={issuedBy}
                onChange={e => setIssuedBy(e.target.value)}
              />
            </label>
            <label>
              Matures on or after
              <Input
                type="date"
                value={maturesAfter}
                onChange={e => setMaturesAfter(e.target.value)}
              />
            </label>
          </div>
        )}
      </div>
      {/* {Object.values(filters)
          .flat()
          .flatMap(key => {
            const option = model.options.find(item => item.key === key);
            return option && (option.termId || option.collectionIds?.length)
              ? [option]
              : [];
          })
          .map(option => (
            <Button
              key={option.key}
              size="small"
              onClick={() => setCollectionNote(option)}
            >
              About {option.label}
            </Button>
          ))} */}
      <div className="ac-results-layout">
        <section className="ac-results">
          <div className="ac-results-heading">
            <strong>{shownRows.length} matching instruments</strong>
            <div className="ac-results-actions">
              {onAdd && (
                <Button
                  disabled={
                    disabled ||
                    !shownRows.some(
                      row => entryAddable(row) && !entrySelected(row),
                    )
                  }
                  onClick={() =>
                    setBulk(
                      shownRows.filter(
                        row => entryAddable(row) && !entrySelected(row),
                      ),
                    )
                  }
                >
                  Review & add matches
                </Button>
              )}
              {selected !== undefined && (
                <Button
                  className="ac-selected-toggle"
                  type={onlySelected ? 'primary' : 'default'}
                  aria-pressed={onlySelected}
                  onClick={() => setOnlySelected(value => !value)}
                >
                  <span className="ac-selected-indicator" aria-hidden="true" />
                  Display only selected
                  <span className="ac-selected-status" aria-hidden="true">
                    {onlySelected ? 'On' : 'Off'}
                  </span>
                </Button>
              )}
            </div>
          </div>
          <CatalogueTable<CatalogueEntry>
            className="ac-instrument-table"
            size="small"
            rowKey="key"
            dataSource={shownRows}
            columns={columns}
            pagination={{
              defaultPageSize: 100,
              showSizeChanger: true,
              pageSizeOptions: [30, 60, 100],
              hideOnSinglePage: true,
            }}
            tableLayout="auto"
            scroll={{ x: '100%' }}
            rowSelection={
              onAdd && onRemove
                ? {
                    selectedRowKeys: shownRows
                      .filter(entrySelected)
                      .map(row => row.key),
                    preserveSelectedRowKeys: true,
                    getCheckboxProps: entry => ({
                      disabled: disabled || !entryAddable(entry),
                      indeterminate: entryPartial(entry),
                      'aria-label': `Select ${entry.family?.code || entry.members[0].ticker}`,
                    }),
                    onSelect: (entry, checked) => {
                      if (disabled || !entryAddable(entry)) return;
                      if (!checked) onRemove(entryIds(entry));
                      else if (entry.family && !isLiveFamily(entry))
                        setBulk([entry]);
                      else onAdd(entryAddIds(entry));
                    },
                    onSelectAll: (checked, _, changed) => {
                      if (disabled) return;
                      const eligible = changed.filter(entryAddable);
                      if (!checked) onRemove(eligible.flatMap(entryIds));
                      else if (
                        eligible.some(
                          entry => entry.family && !isLiveFamily(entry),
                        )
                      )
                        setBulk(eligible);
                      else onAdd(eligible.flatMap(entryAddIds));
                    },
                  }
                : undefined
            }
          />
        </section>
      </div>
      {bulk && onAdd && (
        <AdditionReview
          entries={bulk}
          byId={byId}
          selected={selectedSet}
          context={familyContext}
          disabled={disabled}
          onClose={() => setBulk(null)}
          onAdd={onAdd}
        />
      )}
      {family && (
        <BondFamilyDrawer
          key={family.id}
          family={family}
          showAssetClass={showAssetClass}
          options={model.options}
          members={(
            allEntries.get(`family:${family.id}`)?.members || []
          ).filter(row => !familyContext || selectedSet.has(row.id))}
          matchingIds={new Set(rows.map(row => row.id))}
          selected={selectedSet}
          disabled={disabled}
          onClose={() => setFamily(null)}
          onDetails={setDetail}
          onAdd={familyContext ? undefined : onAdd}
          onRemove={familyContext ? undefined : onRemove}
          selectedExtra={selectedExtra}
          selectedAction={selectedAction}
          onPrepare={
            onAdd && !familyContext
              ? () => {
                  const entry = allEntries.get(`family:${family.id}`);
                  setFamily(null);
                  if (entry) setBulk([entry]);
                }
              : undefined
          }
        />
      )}
      {detail && (
        <AssetDetails
          asset={detail}
          index={data}
          admin={admin}
          sessionId={startedSessionId}
          onClose={() => setDetail(null)}
        />
      )}
      {collectionNote !== null && (
        <CollectionNotes
          option={collectionNote}
          onClose={() => setCollectionNote(null)}
        />
      )}
    </div>
  );
}

function CollectionNotes({
  option,
  onClose,
}: {
  option: FilterOption;
  onClose: () => void;
}) {
  type Note = {
    id: number;
    label: string;
    description: string;
    review_notes?: string;
    source?: string;
  };
  const [content, setContent] = useState<Note[]>();
  const [error, setError] = useState('');
  React.useEffect(() => {
    const controller = new AbortController();
    setContent(undefined);
    setError('');
    import('./api')
      .then(({ catalogueRequest }) =>
        Promise.all(
          (option.termId
            ? [`terms/${option.termId}`]
            : (option.collectionIds || []).map(id => `collections/${id}`)
          ).map(path =>
            catalogueRequest<Note>(path, 'GET', {}, controller.signal),
          ),
        ),
      )
      .then(result => {
        if (!controller.signal.aborted) setContent(result);
      })
      .catch(reason => {
        if (!controller.signal.aborted) setError(reason.message);
      });
    return () => controller.abort();
  }, [option]);
  return (
    <Modal open title={option.label} onCancel={onClose} footer={null}>
      {error ? (
        <Alert type="error" title={error} />
      ) : !content ? (
        <Spin />
      ) : (
        <div className="ac-details">
          {content.map(note => (
            <section className="ac-membership" key={note.id}>
              {content.length > 1 && <strong>{note.label}</strong>}
              {note.description && (
                <p className="ac-prose">{note.description}</p>
              )}
              {note.review_notes && (
                <p className="ac-prose">{note.review_notes}</p>
              )}
              {!note.description && !note.review_notes && (
                <p>No guidance has been added yet.</p>
              )}
              <small>{note.source}</small>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}
